import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundaryFallback from 'components/ErrorBoundaryFallback';
import { DocumentNameInitializer } from 'components/language/DocumentNameInitializer';
import { NabanGlossaryModal } from 'components/NabanGlossaryModal';
import type { MotionConfigProps } from 'framer-motion';
import { MotionConfig } from 'framer-motion';

import { useWatchAppHeight } from '@izimi/hooks';
import { AuthProvider } from 'core/auth/AuthProvider';
import { CitizenPortalTrackingProvider } from 'core/providers/TrackingProvider';
import theme, { Fonts } from 'core/theme';

import Router from './Router';
import '@izimi/components/src/tracking/cookies/styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const motionConfig: MotionConfigProps = {
  reducedMotion: 'user',
  transition: { type: 'tween', duration: 0.15 },
};

const App = () => {
  useWatchAppHeight();

  return (
    <Suspense fallback={null}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <MotionConfig {...motionConfig}>
              <ChakraProvider theme={theme}>
                <CitizenPortalTrackingProvider>
                  <Fonts />
                  <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
                    <Router />
                    <NabanGlossaryModal />
                  </ErrorBoundary>
                  <DocumentNameInitializer />
                </CitizenPortalTrackingProvider>
              </ChakraProvider>
            </MotionConfig>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </Suspense>
  );
};

export default App;
